import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Download, Sparkles, Palette } from "lucide-react";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import logo from "@/assets/arabian-alwaha-logo.jpg";

type CardLanguage = 'en' | 'ar';
type CardTheme = 'classic' | 'gold' | 'silver' | 'festive';

const themes: Record<CardTheme, {
  name: string;
  nameAr: string;
  gradient: string;
  accent: string;
  textAccent: string;
  yearGradient: string;
  pattern: string;
}> = {
  classic: {
    name: 'Classic Green',
    nameAr: 'الأخضر الكلاسيكي',
    gradient: 'from-[#1B5E20] via-[#2E7D32] to-[#D4A574]',
    accent: 'text-[#D4A574]',
    textAccent: 'text-yellow-300',
    yearGradient: 'from-yellow-300 via-[#D4A574] to-yellow-300',
    pattern: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.5) 1px, transparent 0)'
  },
  gold: {
    name: 'Royal Gold',
    nameAr: 'الذهبي الملكي',
    gradient: 'from-[#B8860B] via-[#DAA520] to-[#FFD700]',
    accent: 'text-yellow-100',
    textAccent: 'text-yellow-100',
    yearGradient: 'from-white via-yellow-100 to-white',
    pattern: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.1) 0px, rgba(255,255,255,0.1) 2px, transparent 2px, transparent 12px)'
  },
  silver: {
    name: 'Elegant Silver',
    nameAr: 'الفضي الأنيق',
    gradient: 'from-[#2C3E50] via-[#4A5568] to-[#718096]',
    accent: 'text-gray-200',
    textAccent: 'text-blue-200',
    yearGradient: 'from-gray-200 via-white to-gray-200',
    pattern: 'repeating-linear-gradient(-45deg, rgba(255,255,255,0.05) 0px, rgba(255,255,255,0.05) 1px, transparent 1px, transparent 8px)'
  },
  festive: {
    name: 'Festive Red',
    nameAr: 'الأحمر الاحتفالي',
    gradient: 'from-[#8B0000] via-[#DC143C] to-[#FF6B6B]',
    accent: 'text-yellow-300',
    textAccent: 'text-yellow-200',
    yearGradient: 'from-yellow-200 via-yellow-300 to-yellow-200',
    pattern: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)'
  }
};

const NewYearCard = () => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [cardLanguage, setCardLanguage] = useState<CardLanguage>('en');
  const [cardTheme, setCardTheme] = useState<CardTheme>('classic');

  const currentTheme = themes[cardTheme];

  const handleDownload = async () => {
    if (!cardRef.current) return;
    
    setIsDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: null,
        useCORS: true,
      });
      
      const link = document.createElement('a');
      const langSuffix = cardLanguage === 'ar' ? 'Arabic' : 'English';
      const themeSuffix = cardTheme.charAt(0).toUpperCase() + cardTheme.slice(1);
      link.download = `Arabian-AlWaha-NewYear-2026-${themeSuffix}-${langSuffix}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success(cardLanguage === 'ar' ? 'تم تحميل الصورة بنجاح!' : 'Image downloaded successfully!');
    } catch (error) {
      toast.error(cardLanguage === 'ar' ? 'فشل تحميل الصورة' : 'Failed to download image');
    } finally {
      setIsDownloading(false);
    }
  };

  const isArabic = cardLanguage === 'ar';

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="container mx-auto max-w-2xl">
        <h1 className="text-2xl font-bold text-center mb-6 text-foreground">
          {isArabic ? 'بطاقة تهنئة السنة الجديدة' : 'New Year Greeting Card'}
        </h1>

        {/* Language Toggle */}
        <div className="flex justify-center gap-2 mb-4">
          <Button
            variant={cardLanguage === 'en' ? 'default' : 'outline'}
            onClick={() => setCardLanguage('en')}
            className="min-w-[100px]"
          >
            English
          </Button>
          <Button
            variant={cardLanguage === 'ar' ? 'default' : 'outline'}
            onClick={() => setCardLanguage('ar')}
            className="min-w-[100px]"
          >
            العربية
          </Button>
        </div>

        {/* Theme Selector */}
        <div className="mb-6">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Palette className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {isArabic ? 'اختر التصميم' : 'Choose Theme'}
            </span>
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {(Object.keys(themes) as CardTheme[]).map((theme) => (
              <Button
                key={theme}
                variant={cardTheme === theme ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCardTheme(theme)}
                className="min-w-[90px]"
              >
                {isArabic ? themes[theme].nameAr : themes[theme].name}
              </Button>
            ))}
          </div>
        </div>
        
        {/* Downloadable Card */}
        <div 
          ref={cardRef}
          dir={isArabic ? 'rtl' : 'ltr'}
          className={`relative bg-gradient-to-br ${currentTheme.gradient} rounded-3xl p-8 shadow-2xl overflow-hidden`}
          style={{ aspectRatio: '1/1' }}
        >
          {/* Decorative elements */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-4 left-4 text-4xl">✨</div>
            <div className="absolute top-8 right-8 text-3xl">🎆</div>
            <div className="absolute bottom-12 left-8 text-3xl">🎇</div>
            <div className="absolute top-1/4 right-4 text-2xl">⭐</div>
            <div className="absolute bottom-8 right-12 text-4xl">✨</div>
          </div>
          
          {/* Theme pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: currentTheme.pattern,
              backgroundSize: '30px 30px'
            }}></div>
          </div>

          {/* Content */}
          <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white">
            {/* Logo */}
            <div className="w-24 h-24 rounded-full bg-white p-2 shadow-xl mb-6">
              <img 
                src={logo} 
                alt={isArabic ? 'شعار الواحة العربية' : 'Arabian Al Waha Logo'}
                className="w-full h-full object-contain rounded-full"
              />
            </div>
            
            {/* Company Name */}
            <h2 className={`text-2xl font-bold mb-2 ${currentTheme.accent} drop-shadow-lg`}>
              {isArabic ? 'مؤسسة الواحة العربية' : 'Arabian Al Waha Est'}
            </h2>
            
            {/* Year */}
            <div className={`text-7xl font-extrabold mb-4 bg-gradient-to-r ${currentTheme.yearGradient} bg-clip-text text-transparent drop-shadow-2xl`}>
              2026
            </div>
            
            {/* Greeting */}
            <h3 className="text-3xl font-bold mb-3 drop-shadow-lg">
              {isArabic ? 'سنة جديدة سعيدة!' : 'Happy New Year!'}
            </h3>
            
            <p className="text-xl mb-4 text-white/90 font-medium">
              {isArabic ? 'Happy New Year' : 'سنة جديدة سعيدة'}
            </p>
            
            {/* Message */}
            <p className="text-base text-white/80 max-w-sm leading-relaxed">
              {isArabic 
                ? 'نتمنى لكم عاماً مليئاً بالنجاح والازدهار والسعادة. شكراً لثقتكم المستمرة وشراكتكم الكريمة.'
                : 'Wishing you success, prosperity, and happiness in the coming year. Thank you for your continued trust and partnership.'
              }
            </p>
            
            {/* Sparkle decoration */}
            <div className="flex gap-2 mt-6">
              <Sparkles className={`w-6 h-6 ${currentTheme.textAccent} animate-pulse`} />
              <Sparkles className={`w-6 h-6 ${currentTheme.accent} animate-pulse delay-100`} />
              <Sparkles className={`w-6 h-6 ${currentTheme.textAccent} animate-pulse delay-200`} />
            </div>
          </div>
        </div>

        {/* Download Button */}
        <div className="mt-8 flex justify-center">
          <Button 
            size="lg" 
            onClick={handleDownload}
            disabled={isDownloading}
            className="bg-accent hover:bg-accent/90 text-white px-8 py-4 text-lg font-semibold shadow-xl"
          >
            <Download className="w-5 h-5 mr-2" />
            {isDownloading 
              ? (isArabic ? 'جاري التحميل...' : 'Downloading...') 
              : (isArabic ? 'تحميل للواتساب' : 'Download for WhatsApp')
            }
          </Button>
        </div>
        
        <p className="text-center text-muted-foreground mt-4 text-sm">
          {isArabic 
            ? 'اضغط على الزر أعلاه لتحميل بطاقة التهنئة ومشاركتها مع عملائك'
            : 'Click the button above to download the greeting card and share it with your clients'
          }
        </p>
      </div>
    </div>
  );
};

export default NewYearCard;
